import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, MessageSquare } from "lucide-react";
import { LOGO_SRC, NAV_LINKS, TYPEFORM, withUTM } from "../data/landing";

const utmPage = (pathname) => (pathname === "/" ? "for-fans" : pathname.replace(/^\//, ""));

export default function NavBar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const page = utmPage(location.pathname);
  const T = Object.fromEntries(Object.entries(TYPEFORM).map(([k, v]) => [k, withUTM(v, page)]));

  // Close drawer on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Esc key dismiss
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const closeDrawer = () => setOpen(false);

  const drawer =
    typeof document !== "undefined" ? (
      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu-overlay"
            className="md:hidden fixed inset-0 z-[9999]"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
            style={{ height: "100dvh" }}
          >
            {/* backdrop */}
            <motion.button
              key="mobile-menu-backdrop"
              type="button"
              aria-label="Close menu"
              data-testid="mobile-menu-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={closeDrawer}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              style={{ height: "100dvh" }}
            />

            {/* drawer panel */}
            <motion.aside
              key="mobile-menu-drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              role="dialog"
              aria-modal="true"
              aria-label="Site menu"
              data-testid="mobile-menu-drawer"
              className="absolute top-0 right-0 w-[88%] max-w-[380px] bg-white shadow-[0_30px_60px_-15px_rgba(0,0,0,0.35)] flex flex-col"
              style={{ height: "100dvh" }}
            >
              {/* drawer header */}
              <div className="flex items-center justify-between px-5 h-[72px] border-b border-black/5 flex-shrink-0">
                <img src={LOGO_SRC} alt="eGeez" className="h-8 w-auto" draggable={false} />
                <button
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                  onClick={closeDrawer}
                  className="p-2 rounded-full hover:bg-black/5 active:scale-95 transition"
                >
                  <X size={22} strokeWidth={2.4} />
                </button>
              </div>

              {/* drawer nav links */}
              <nav className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
                <ul className="space-y-0">
                  {NAV_LINKS.map((l, i) => {
                    const isActive =
                      location.pathname === l.href ||
                      (l.href === "/for-fans" && location.pathname === "/");
                    return (
                      <motion.li
                        key={l.href}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          duration: 0.3,
                          delay: 0.05 + i * 0.04,
                          ease: [0.16, 1, 0.3, 1],
                        }}
                      >
                        <Link
                          to={l.href}
                          data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                          onClick={closeDrawer}
                          className={`flex items-center justify-between py-3.5 border-b border-black/5 text-[18px] font-semibold transition-colors ${
                            isActive
                              ? "text-[#0041E6]"
                              : "text-black hover:text-[#0041E6]"
                          }`}
                        >
                          <span>{l.label}</span>
                          <ArrowUpRight
                            size={18}
                            className={isActive ? "text-[#0041E6]" : "text-black/30"}
                          />
                        </Link>
                      </motion.li>
                    );
                  })}
                </ul>
              </nav>

              {/* drawer CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="p-4 sm:p-5 border-t border-black/5 space-y-2.5 bg-[#FAFAFA] flex-shrink-0"
              >
                <a
                  href={T.joinWaitlist}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-cta-join-waitlist"
                  onClick={closeDrawer}
                  className="group flex items-center justify-between w-full bg-[#0041E6] hover:bg-[#0033B8] text-white font-bold text-[15px] rounded-full pl-5 pr-2 h-[48px] transition shadow-[0_14px_30px_-12px_rgba(0,65,230,0.55)]"
                >
                  Join the Waitlist
                  <span className="w-[36px] h-[36px] rounded-full bg-white text-[#0041E6] flex items-center justify-center">
                    <ArrowUpRight size={16} />
                  </span>
                </a>
                <a
                  href={T.tellMeAnything}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-cta-tell-us-anything"
                  onClick={closeDrawer}
                  className="group flex items-center justify-between w-full bg-white hover:bg-black hover:text-white text-black font-bold text-[15px] rounded-full pl-5 pr-2 h-[48px] border-2 border-black/10 hover:border-black transition"
                >
                  <span className="flex items-center gap-2">
                    <MessageSquare size={15} strokeWidth={2.4} />
                    Tell Us Anything
                  </span>
                  <span className="w-[36px] h-[36px] rounded-full bg-black text-white flex items-center justify-center group-hover:bg-[#FFD400] group-hover:text-black transition">
                    <ArrowUpRight size={16} />
                  </span>
                </a>
              </motion.div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    ) : null;

  return (
    <header
      className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b border-black/5"
      data-testid="site-navbar"
    >
      <div className="mx-auto max-w-[1440px] px-5 md:px-12 h-[88px] flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center">
          <img src={LOGO_SRC} alt="eGeez" className="h-10 md:h-12 w-auto" draggable={false} />
        </Link>

        {/* Desktop nav — unchanged */}
        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => {
            const isActive =
              location.pathname === l.href ||
              (l.href === "/for-fans" && location.pathname === "/");
            return (
              <NavLink
                key={l.href}
                to={l.href}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={`text-[18px] font-medium transition-colors ${
                  isActive ? "text-[#0041E6]" : "text-black hover:text-[#0041E6]"
                }`}
              >
                {l.label}
              </NavLink>
            );
          })}
          {location.pathname === "/for-licensees" ? (
            <a
              href={T.signUp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-sign-up"
              className="bg-[#0041E6] hover:bg-[#0033B8] text-white font-medium text-[18px] rounded-full px-7 h-[45px] inline-flex items-center justify-center transition shadow-[0_8px_20px_-8px_rgba(0,65,230,0.5)]"
            >
              Sign-Up
            </a>
          ) : location.pathname === "/for-licensors" ? (
            <a
              href={T.letsTalk}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-lets-talk"
              className="bg-[#0041E6] hover:bg-[#0033B8] text-white font-medium text-[18px] rounded-full px-7 h-[45px] inline-flex items-center justify-center transition shadow-[0_8px_20px_-8px_rgba(0,65,230,0.5)]"
            >
              Let&apos;s Talk
            </a>
          ) : (
            <a
              href={T.joinWaitlist}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-join-waitlist"
              className="bg-[#0041E6] hover:bg-[#0033B8] text-white font-medium text-[18px] rounded-full px-7 h-[45px] inline-flex items-center justify-center transition shadow-[0_8px_20px_-8px_rgba(0,65,230,0.5)]"
            >
              Join Waitlist
            </a>
          )}
        </nav>

        {/* Mobile — hamburger only */}
        <button
          aria-label="Open menu"
          aria-expanded={open}
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 rounded-full hover:bg-black/5 active:scale-95 transition"
          onClick={() => setOpen(true)}
        >
          <Menu size={26} strokeWidth={2.4} />
        </button>
      </div>

      {/* Mobile slide-in drawer rendered via Portal so it escapes the sticky header */}
      {drawer && createPortal(drawer, document.body)}
    </header>
  );
}
